import { redirect } from '@tanstack/react-router';
import { env } from 'config/env';
import { posthog } from 'config/posthog';

import type { AuthMeResponse } from '@brightlocal/api-client';

import { type FeatureFlagKey } from 'shared/consts';

import { resolveFeatureFlag } from './resolveFeatureFlag';

/**
 * Max time we wait for PostHog to finish loading feature flags before giving
 * up and letting navigation proceed. Picked to feel instantaneous when flags
 * are cached, while still giving the `/flags` endpoint a fair chance on a
 * cold start. Callers must handle `undefined` flag values when the timeout
 * fires so a PostHog outage can't permanently block navigation.
 */
export const FLAGS_WAIT_TIMEOUT_MS = 3000;

/**
 * Waits until posthog.onFeatureFlags runs.
 *
 * If `skipFirstFire` is true, it ignores the first call
 * (which happens immediately with cached flags) and only resolves after the next call
 * (after a reload).
 *
 * `flagsReady` is stored in a ref-like container because
 * `onFeatureFlags` might call the handler right away—before the unsubscribe function
 * has been assigned.
 */
const awaitFlagsCallback = (timeoutMs: number, skipFirstFire: boolean): Promise<void> => {
  if (!env.posthog.enabled) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const disposer: { unsubscribe?: () => void } = {};
    let settled = false;
    let stalePending = skipFirstFire;

    const finish = () => {
      if (settled) return;
      settled = true;
      globalThis.clearTimeout(timeoutId);
      disposer.unsubscribe?.();
      resolve();
    };

    const timeoutId = globalThis.setTimeout(finish, timeoutMs);

    disposer.unsubscribe = posthog.onFeatureFlags(() => {
      // PostHog invokes handlers synchronously with the currently-cached
      // values before any reload triggered by `identify` completes. Skip
      // that first invocation when the caller specifically asked for fresh
      // flags so we don't resolve with stale state.
      if (stalePending) {
        stalePending = false;
        return;
      }
      finish();
    });

    // If the callback fired synchronously above, `finish()` ran before the
    // return value was assigned — retroactively unsubscribe now.
    if (settled) {
      disposer.unsubscribe();
    }
  });
};

/**
 * Waits for PostHog to load feature flags and resolves when they're ready.
 * This resolves on the first callback (either sync or async), or after `timeoutMs` if flags are not loaded.
 * Does nothing if PostHog is disabled.
 * To wait for only new flags after identify, use {@link waitForFreshFeatureFlags}.
 */
export const waitForFeatureFlags = (timeoutMs: number = FLAGS_WAIT_TIMEOUT_MS) => awaitFlagsCallback(timeoutMs, false);

/**
 * Works like {@link waitForFeatureFlags}, but ignores the first callback with cached flags.
 * Use right after `posthog.identify(...)` to wait until new feature flags are loaded for the updated user.
 */
export const waitForFreshFeatureFlags = (timeoutMs: number = FLAGS_WAIT_TIMEOUT_MS) =>
  awaitFlagsCallback(timeoutMs, true);

export interface RequireFeatureFlagOptions {
  /** Side-effect invoked when the flag is disabled or unknown. */
  onDisabled?: () => void | Promise<void>;
  /**
   * TanStack Router target used for the safety-net `throw redirect(...)` that
   * prevents the gated component from mounting while `onDisabled` is still
   * tearing down the current document. Defaults to the app root.
   */
  fallbackRoute?: string;
}

/**
 * Route-level feature-flag guard for TanStack Router `beforeLoad`. Waits for
 * PostHog to settle, then resolves the flag via {@link resolveFeatureFlag}
 * (SDK value first, `backendFlags` — pass `context.authUser.featureAllowance`
 * — as fallback). When the flag resolves disabled, runs the caller's
 * `onDisabled` side effect and throws a router redirect so the gated
 * component never renders.
 *
 * If `onDisabled` performs a hard browser navigation (e.g.
 * `bounceToOldPlatform`), it is expected to never resolve — the route's
 * `pendingComponent` stays visible until the browser commits the nav, so no
 * fallback route flashes on screen. When `onDisabled` resolves normally (SPA
 * redirect, no-op, etc.), the safety-net `redirect` fires so the gated
 * component still never mounts.
 */
export const requireFeatureFlag = async (
  flag: FeatureFlagKey,
  backendFlags: AuthMeResponse['featureAllowance'],
  options?: RequireFeatureFlagOptions
) => {
  await waitForFeatureFlags();

  if (resolveFeatureFlag(posthog.isFeatureEnabled(flag), backendFlags[flag])) return;

  await options?.onDisabled?.();
  throw redirect({ to: options?.fallbackRoute ?? '/' });
};
